import { Fragment, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";

import { ILocationGroup } from "../../models/LocationGroup";

export default function LocationGroupsTable({ locationGroups, availabilities, setLocationGroup, moveLocationGroup, isReordering }: { locationGroups: ILocationGroup[], availabilities: Record<string, { locationCount: number, capacity: number, vehicleCount: number }>, setLocationGroup: any, moveLocationGroup: (locationGroupId: string, direction: "up" | "down") => void, isReordering: boolean }) {
  // Default to the display order, so the table opens showing the sequence the capacity cards use.
  const [sorting, setSorting] = useState<SortingState>([{ id: "order", desc: false }]);
  const colHelper = createColumnHelper<ILocationGroup>();

  // The move buttons act on the display order, not on the row position - a user who has sorted the
  // table by name would otherwise see "up" move a section somewhere unrelated. These are the ends of
  // that sequence, so the buttons can disable themselves correctly.
  const orderedIds = useMemo(
    () => [...locationGroups].sort((a, b) => a.order - b.order).map((group) => group.id),
    [locationGroups]
  );

  const cols = [
    colHelper.accessor("order", {
      id: "order",
      header: "Order",
      sortingFn: "auto",
      cell: (info) => <span>{info.row.original.order}</span>
    }),
    colHelper.display({
      id: "name",
      header: "Name",
      sortingFn: "auto",
      cell: (info) => (
        <span className="flex  " >
          <span className="ml-4 m-auto">{info.row.original.name}</span>
        </span>
      )
    }),
    colHelper.display({
      id: "description",
      header: "Description",
      sortingFn: "auto",
      cell: (info) => info.row.original.description || "-"
    }),
    colHelper.display({
      id: "locations",
      header: "Locations",
      sortingFn: "auto",
      cell: (info) => availabilities[info.row.original.id]?.locationCount ?? 0
    }),
    colHelper.display({
      id: "availability",
      header: "Availability",
      sortingFn: "auto",
      cell: (info) => {
        const availability = availabilities[info.row.original.id];
        const count = availability?.vehicleCount ?? 0;
        const capacity = availability?.capacity ?? 0;

        if (capacity > 0) {
          return `${count}/${capacity}`;
        }

        return count;
      }
    }),
  ];

  const table = useReactTable({
    data: locationGroups,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - very similar to all the other tables */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
                <th
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  Move
                </th>
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
          {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => setLocationGroup(row.original)}
                    className={"hover:bg-gray-100 hover:cursor-pointer"}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td className="px-3 py-3 border text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLocationGroup(row.original.id, "up");
                          }}
                          disabled={isReordering || orderedIds[0] === row.original.id}
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLocationGroup(row.original.id, "down");
                          }}
                          disabled={isReordering || orderedIds[orderedIds.length - 1] === row.original.id}
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
}
